import {
  getChartViewportRevision,
  subscribeChartViewport,
} from 'features/timeSeries/utils/chartViewportSignal'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import type uPlot from 'uplot'

import type {
  OverlayAlert,
  PositionedOverlayAlert,
} from './mdDraftAlertCaseFlowTypes'
import {
  buildAlertBadgeLanes,
  getPlotBounds,
} from './mdDraftAlertCaseFlowUtils'

interface UseManualAlertOverlayArgs {
  overlayAlerts: readonly OverlayAlert[]
  uPlotRef: React.MutableRefObject<uPlot | null>
  chartAreaRef: React.MutableRefObject<HTMLDivElement | null>
  xAxisSensorId: string | null
  initialManualAlertId?: string
  capturePrimarySensorId: string
  chartDataLength: number
  chartDataExtentKey: string
  selectedSensorsLength: number
  isChartLoading: boolean
  setHighlightedSensorId: (sensorId: string | null) => void
  setSecondaryHighlightedSensorIds: (sensorIds: string[]) => void
}

type RenderedXRange = {
  min: number
  max: number
}

const getFiniteTime = (value: string): number | null => {
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : null
}

const getRenderedXRange = (
  uPlotInstance: uPlot | null
): RenderedXRange | null => {
  if (!uPlotInstance) {
    return null
  }

  const scaleMin = Number(uPlotInstance.scales.x?.min)
  const scaleMax = Number(uPlotInstance.scales.x?.max)
  if (Number.isFinite(scaleMin) && Number.isFinite(scaleMax)) {
    return {
      min: Math.min(scaleMin, scaleMax),
      max: Math.max(scaleMin, scaleMax),
    }
  }

  const xValues = Array.isArray(uPlotInstance.data?.[0])
    ? uPlotInstance.data[0]
    : []
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  xValues.forEach((value) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return
    }

    min = Math.min(min, value)
    max = Math.max(max, value)
  })

  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null
}

const alertOverlapsRenderedXRange = (
  alert: OverlayAlert,
  range: RenderedXRange
): boolean => {
  const renderStart = getFiniteTime(alert.renderStartAt)
  const renderEnd = getFiniteTime(alert.renderEndAt)
  if (renderStart === null || renderEnd === null) {
    return false
  }

  return (
    Math.max(renderStart, renderEnd) >= range.min &&
    Math.min(renderStart, renderEnd) <= range.max
  )
}

export function useManualAlertOverlay({
  overlayAlerts,
  uPlotRef,
  chartAreaRef,
  xAxisSensorId,
  initialManualAlertId,
  capturePrimarySensorId,
  chartDataLength,
  chartDataExtentKey,
  selectedSensorsLength,
  isChartLoading,
  setHighlightedSensorId,
  setSecondaryHighlightedSensorIds,
}: UseManualAlertOverlayArgs) {
  const [hoveredAlertId, setHoveredAlertId] = useState<string | null>(null)
  const [overlayRevision, setOverlayRevision] = useState(0)
  const hoverLeaveTimeoutRef = useRef<number | null>(null)

  const overlayAlertById = useMemo(
    () => new Map(overlayAlerts.map((alert) => [alert.id, alert])),
    [overlayAlerts]
  )

  // Badges are positioned by projecting their timestamps through the live uPlot
  // instance, so they are only correct for as long as that instance's x scale
  // stays put. The chart now moves its scales in place (and tweens them across
  // frames) instead of being destroyed and rebuilt on every range change, so
  // subscribe to the movement directly rather than inferring it from a
  // teardown. Missing a move would strand a badge at the previous window's
  // pixel — an alert drawn at a timestamp it did not happen at.
  const hasOverlayAlerts = overlayAlerts.length > 0
  const subscribeToViewport = useCallback(
    (onViewportChange: () => void) =>
      hasOverlayAlerts ? subscribeChartViewport(onViewportChange) : () => {},
    [hasOverlayAlerts]
  )
  const getViewportSnapshot = useCallback(
    () => (hasOverlayAlerts ? getChartViewportRevision() : 0),
    [hasOverlayAlerts]
  )
  const viewportRevision = useSyncExternalStore(
    subscribeToViewport,
    getViewportSnapshot,
    getViewportSnapshot
  )

  useEffect(() => {
    if (!initialManualAlertId) {
      return
    }

    setHoveredAlertId(initialManualAlertId)
    const timeoutId = window.setTimeout(() => {
      setHoveredAlertId((current) =>
        current === initialManualAlertId ? null : current
      )
    }, 1800)

    return () => window.clearTimeout(timeoutId)
  }, [initialManualAlertId])

  useEffect(() => {
    if (!chartAreaRef.current) {
      return
    }

    const observer = new ResizeObserver(() => {
      setOverlayRevision((current) => current + 1)
    })

    observer.observe(chartAreaRef.current)
    return () => observer.disconnect()
  }, [chartAreaRef])

  useEffect(() => {
    let frameOne = 0
    let frameTwo = 0

    frameOne = window.requestAnimationFrame(() => {
      frameTwo = window.requestAnimationFrame(() => {
        setOverlayRevision((current) => current + 1)
      })
    })

    return () => {
      window.cancelAnimationFrame(frameOne)
      window.cancelAnimationFrame(frameTwo)
    }
  }, [
    chartDataExtentKey,
    chartDataLength,
    selectedSensorsLength,
    isChartLoading,
  ])

  const activeOverlayAlert = hoveredAlertId
    ? (overlayAlertById.get(hoveredAlertId) ?? null)
    : null

  useEffect(() => {
    const highlightPrimarySensorId =
      capturePrimarySensorId || activeOverlayAlert?.primarySensorId || null
    const secondarySensorIds = activeOverlayAlert
      ? activeOverlayAlert.sensorIds.filter(
          (sensorId) => sensorId !== activeOverlayAlert.primarySensorId
        )
      : []

    setHighlightedSensorId(highlightPrimarySensorId)
    setSecondaryHighlightedSensorIds(secondarySensorIds)

    return () => {
      setHighlightedSensorId(null)
      setSecondaryHighlightedSensorIds([])
    }
  }, [
    activeOverlayAlert,
    capturePrimarySensorId,
    setHighlightedSensorId,
    setSecondaryHighlightedSensorIds,
  ])

  const clearHoverLeaveTimeout = useCallback(() => {
    if (hoverLeaveTimeoutRef.current !== null) {
      window.clearTimeout(hoverLeaveTimeoutRef.current)
      hoverLeaveTimeoutRef.current = null
    }
  }, [])

  useEffect(() => () => clearHoverLeaveTimeout(), [clearHoverLeaveTimeout])

  const startHoverAlert = useCallback(
    (alertId: string) => {
      clearHoverLeaveTimeout()
      setHoveredAlertId(alertId)
    },
    [clearHoverLeaveTimeout]
  )

  const scheduleHoverAlertHide = useCallback(
    (alertId: string) => {
      clearHoverLeaveTimeout()
      hoverLeaveTimeoutRef.current = window.setTimeout(() => {
        setHoveredAlertId((current) => (current === alertId ? null : current))
        hoverLeaveTimeoutRef.current = null
      }, 90)
    },
    [clearHoverLeaveTimeout]
  )

  const clearHoverAlert = useCallback(() => {
    clearHoverLeaveTimeout()
    setHoveredAlertId(null)
  }, [clearHoverLeaveTimeout])

  const positionedAlerts: PositionedOverlayAlert[] = useMemo(() => {
    void overlayRevision
    void chartDataExtentKey
    void viewportRevision
    const uPlotInstance = uPlotRef.current
    const plotBounds = getPlotBounds(uPlotInstance, chartAreaRef.current)
    // During cached navigation the chart can be loading a new request while the
    // previous plot is intentionally still rendered. Keep case annotations
    // attached to that visible plot; only hold off when there is no plotted data
    // to anchor against yet.
    const hasPlottedData = chartDataLength > 0
    if (!uPlotInstance || !plotBounds || xAxisSensorId || !hasPlottedData) {
      return []
    }
    const renderedXRange = getRenderedXRange(uPlotInstance)
    if (!renderedXRange) {
      return []
    }
    const renderedOverlayAlerts = overlayAlerts.filter((alert) =>
      alertOverlapsRenderedXRange(alert, renderedXRange)
    )

    const laneById = new Map(
      buildAlertBadgeLanes(
        renderedOverlayAlerts.map((alert) => ({
          id: alert.id,
          startAt: alert.focusStartAt,
          endAt: alert.focusEndAt,
        }))
      ).map((lane) => [lane.alertId, lane.lane])
    )

    return renderedOverlayAlerts.flatMap((alert) => {
      const renderStartX =
        uPlotInstance.valToPos(
          new Date(alert.renderStartAt).getTime(),
          'x',
          false
        ) + plotBounds.left
      const renderEndX =
        uPlotInstance.valToPos(
          new Date(alert.renderEndAt).getTime(),
          'x',
          false
        ) + plotBounds.left
      const focusStartX =
        uPlotInstance.valToPos(
          new Date(alert.focusStartAt).getTime(),
          'x',
          false
        ) + plotBounds.left
      const focusEndX =
        uPlotInstance.valToPos(
          new Date(alert.focusEndAt).getTime(),
          'x',
          false
        ) + plotBounds.left

      const left = Math.max(plotBounds.left, Math.min(renderStartX, renderEndX))
      const right = Math.min(
        plotBounds.right,
        Math.max(renderStartX, renderEndX)
      )
      const width = Math.max(0, right - left)
      if (width <= 0) {
        return []
      }

      const focusLeft = Math.max(
        plotBounds.left,
        Math.min(focusStartX, focusEndX)
      )
      const focusRight = Math.min(
        plotBounds.right,
        Math.max(focusStartX, focusEndX)
      )
      const focusWidth = Math.max(2, focusRight - focusLeft)
      const lane = laneById.get(alert.id) ?? 0
      const badgeLeft = Math.min(
        plotBounds.right - 168,
        Math.max(plotBounds.left + 4, focusLeft + 4)
      )
      const detailLeft = Math.min(
        plotBounds.right - 220,
        Math.max(plotBounds.left + 4, badgeLeft - 8)
      )
      const detailTop = Math.min(
        plotBounds.bottom - 144,
        Math.max(plotBounds.top + 28, plotBounds.top + 30 + lane * 22)
      )

      return [
        {
          ...alert,
          plotTop: plotBounds.top,
          plotHeight: plotBounds.height,
          left,
          width,
          focusLeft,
          focusWidth,
          badgeLeft,
          badgeTop: plotBounds.top + 8 + lane * 22,
          detailLeft,
          detailTop,
        },
      ]
    })
  }, [
    overlayAlerts,
    overlayRevision,
    chartAreaRef,
    uPlotRef,
    xAxisSensorId,
    chartDataLength,
    chartDataExtentKey,
    viewportRevision,
  ])

  const positionedAlertById = useMemo(
    () => new Map(positionedAlerts.map((alert) => [alert.id, alert])),
    [positionedAlerts]
  )

  const activePositionedAlert = activeOverlayAlert
    ? (positionedAlertById.get(activeOverlayAlert.id) ?? null)
    : null

  return {
    positionedAlerts,
    activePositionedAlert,
    startHoverAlert,
    scheduleHoverAlertHide,
    clearHoverAlert,
  }
}
